import { MobileSignInCheckResult } from '../../domain/mobile-sign-in';
import { MobileSignInQrCodeService } from '../../services/mobile-sign-in-qr-code.service';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { AsyncState } from '@ppl/store';
import { PPL_DIALOG_DATA, PplDialogRef } from '@ppl/ui/dialog';
import { Unsubscribe } from '@ppl/utils/angular.utils';
import { defaultRetryStrategy, pollWhile } from '@ppl/utils/polling.utils';
import { unsubscribe } from '@ppl/utils/subscription.utils';
import { BehaviorSubject } from 'rxjs';
import { retry } from 'rxjs/operators';
import type { OnDestroy, OnInit } from '@angular/core';
import type { AsyncValue } from '@ppl/store';
import type { Subscription } from 'rxjs';

@Component({
  selector: 'ppl-mobile-sign-in-qr-code-dialog',
  templateUrl: './mobile-sign-in-qr-code-dialog.component.html',
  styleUrl: './mobile-sign-in-qr-code-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
@Unsubscribe()
export class MobileSignInQrCodeDialogComponent implements OnInit, OnDestroy {

  data$ = new BehaviorSubject<AsyncValue<{}>>({ state: AsyncState.IDLE });
  activationExpiration$ = new BehaviorSubject(DEFAULT_EXPIRY_TIME_IN_SECONDS);
  success$ = new BehaviorSubject(false);

  dataSubscription: Subscription;
  checkActivationStatusSubscription: Subscription;
  checkActivationExpiredTimer: any;
  securityTokenExpiration: Subscription;

  AsyncState = AsyncState;

  constructor(
    private dialogRef: PplDialogRef,
    @Inject(PPL_DIALOG_DATA) public data: MobileSignInQrCodeDialogData,
    private mobileSignInQrCodeService: MobileSignInQrCodeService
  ) { }

  ngOnInit() {
    this.createQrCode();

    this.securityTokenExpiration = this.mobileSignInQrCodeService.securityTokenExpired$.subscribe(data => {
      this.data.onSessionExpiration(data);
    });
  }

  ngOnDestroy() {
    clearTimeout(this.checkActivationExpiredTimer);
  }

  createQrCode() {
    unsubscribe(this.dataSubscription);

    this.data$.next({
      state: AsyncState.FETCHING
    });

    this.dataSubscription = this.mobileSignInQrCodeService.createQrCode({
      securityToken: this.data.securityToken
    }).subscribe(
      data => {
        this.data$.next({
          state: AsyncState.FETCHED,
          value: data
        });

        this.startCheckingActivationStatusAndExpiration(data.id);
      },
      error => {
        this.data$.next({
          state: AsyncState.ERROR,
          error
        });
      }
    );
  }

  onClose() {
    this.dialogRef.close();
  }

  private startCheckingActivationStatusAndExpiration(id: number) {
    const checkIfExpired = () => {
      const remainingSeconds = Math.max(0, this.activationExpiration$.value - 1);
      if (remainingSeconds === 0) {
        unsubscribe(this.checkActivationStatusSubscription);
        this.createQrCode();
      } else {
        this.activationExpiration$.next(remainingSeconds);
        this.checkActivationExpiredTimer = setTimeout(() => {
          checkIfExpired();
        }, 1000);
      }
    };

    this.startCheckingActivationStatus(id);
    this.activationExpiration$.next(DEFAULT_EXPIRY_TIME_IN_SECONDS);
    checkIfExpired();
  }

  private startCheckingActivationStatus(id: number) {
    this.checkActivationStatusSubscription = pollWhile(
      this.mobileSignInQrCodeService.checkStatus({
        id,
        securityToken: this.data.securityToken
      }),
      response => response.result !== MobileSignInCheckResult.InProgress,
      { pollPeriod: 3000 }
    ).pipe(
      retry(defaultRetryStrategy()),
    ).subscribe({
      next: ([response, isDone]) => {
        if (isDone) {
          if (response.result === MobileSignInCheckResult.Finished) {
            this.stopCheckingActivationStatus();
            this.success$.next(true);
          } else if (response.result === MobileSignInCheckResult.Failed) {
            this.stopCheckingActivationStatus();
          }
        }
      }
    });
  }

  private stopCheckingActivationStatus() {
    clearTimeout(this.checkActivationExpiredTimer);
    unsubscribe(this.checkActivationStatusSubscription);
  }

}

const DEFAULT_EXPIRY_TIME_IN_SECONDS = 60;

export interface MobileSignInQrCodeDialogData {
  onSessionExpiration: (data: { securityToken: string; }) => void;
  securityToken: string;
}
